/** Maps sheet coordinates (mm) to screen pixels: screen = sheet * scale + offset. */
export class Viewport {
  offsetX: number;
  offsetY: number;
  /** Pixels per millimetre. */
  scale: number;
  minScale = 0.05;
  maxScale = 200;

  constructor(offsetX = 0, offsetY = 0, scale = 4) {
    this.offsetX = offsetX;
    this.offsetY = offsetY;
    this.scale = scale;
  }

  toSheet(sx: number, sy: number) {
    return { x: (sx - this.offsetX) / this.scale, y: (sy - this.offsetY) / this.scale };
  }

  toScreen(x: number, y: number) {
    return { x: x * this.scale + this.offsetX, y: y * this.scale + this.offsetY };
  }

  panBy(dx: number, dy: number): void {
    this.offsetX += dx;
    this.offsetY += dy;
  }

  /** Zoom by `factor`, keeping the sheet point under (sx, sy) fixed on screen. */
  zoomAt(sx: number, sy: number, factor: number): void {
    const before = this.toSheet(sx, sy);
    this.scale = Math.min(this.maxScale, Math.max(this.minScale, this.scale * factor));
    this.offsetX = sx - before.x * this.scale;
    this.offsetY = sy - before.y * this.scale;
  }

  /** Fit sheet-space bounds into a screen rectangle with a margin in pixels. */
  fit(
    b: { minX: number; minY: number; maxX: number; maxY: number },
    width: number,
    height: number,
    margin = 40,
  ) {
    const w = Math.max(b.maxX - b.minX, 1);
    const h = Math.max(b.maxY - b.minY, 1);
    this.scale = Math.min(
      this.maxScale,
      Math.max(this.minScale, Math.min((width - 2 * margin) / w, (height - 2 * margin) / h)),
    );
    this.offsetX = (width - w * this.scale) / 2 - b.minX * this.scale;
    this.offsetY = (height - h * this.scale) / 2 - b.minY * this.scale;
  }

  /** Visible sheet-space rectangle for a canvas of the given pixel size. */
  visibleBounds(width: number, height: number) {
    const a = this.toSheet(0, 0);
    const b = this.toSheet(width, height);
    return { minX: a.x, minY: a.y, maxX: b.x, maxY: b.y };
  }
}
